import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FlamingoHttpService } from '@flamingo/service/flamingo-http.service';
import { FlamingoMessageService } from '@flamingo/service/flamingo-message.service';
import { fuseAnimations } from '@fuse/animations';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { AuctionView } from '../../../../../../../../core/model/auction-view.model';
import { BuyerAccountView } from '../../../../../../../../core/model/buyer-account-view.model';
import { BuyerFundView } from '../../../../../../../../core/model/buyer-fund-view.model';
import { UserType } from '../../../../../../../../core/model/user.model';
import { VirtualBalance } from '../../../../../../../../core/model/virtual-balance.model';
import { WalletDetail } from '../../../../../../../../core/model/wallet-detail.model';
import { BuyerService } from '../../../../buyer.service';
import { locale as english } from './i18n/en';
import { locale as italian } from './i18n/it';

@Component({
  selector: 'app-auction-bid-dialog',
  templateUrl: './auction-bid-dialog.component.html',
  styles: [
    `
        mat-dialog-content {
            max-height: 70vh;
        }
        .warning-card {
            background-color: rgba(255, 152, 0, 0.12);
            border-left: 4px solid #ff9800;
        }
        .warning-icon {
            color: #ff9800;
            vertical-align: middle;
        }
    `,
  ],
  animations: [
    trigger('addressExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('500ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
    fuseAnimations,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class AuctionBidDialogComponent implements OnInit {
  auction: AuctionView;
  interest: any;
  bidInterestRateTemp: number;
  step = 0.00025;
  auctionBidFormGroup: UntypedFormGroup;

  showInvoice = false;
  showPassword = false;
  isSending = false;
  hasInstallmentsInvoice = false;

  selectedFund?: BuyerFundView;
  wallet: WalletDetail;
  virtualBalance: VirtualBalance;
  buyer: BuyerAccountView;

  /** Increments on each fund change so in-flight HTTP responses cannot apply to the wrong wallet. */
  private walletRequestGeneration = 0;

  UserType = UserType;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AuctionBidDialogModel,
    private _buyerService: BuyerService,
    private _dialogRef: MatDialogRef<AuctionBidDialogComponent>,
    private _formBuilder: UntypedFormBuilder,
    private _httpService: FlamingoHttpService,
    private _messageService: FlamingoMessageService,
    private _translateService: TranslateService,
    private _translationLoader: FuseTranslationLoaderService,
  ) {
    this._translationLoader.loadTranslations(italian, english);
  }

  ngOnInit(): void {
    this.buyer = this._buyerService.user;
    this.selectedFund = this._buyerService.userSelectedFund;
    this.updateWallet();
    this.showPassword = this._buyerService.user.hasTransactionPassword;
    this.auction = this.data.auction;
    this.hasInstallmentsInvoice = this.hasInstallments(this.auction);
    this.interest = this.auction.maxBidInterestRate
      ? this.auction.maxBidInterestRate - this.step
      : this.auction.nominalMaxInterestRate;

    this.bidInterestRateTemp = this.interest;

    this.auctionBidFormGroup = this._formBuilder.group(
      {
        bidInterestRate: new UntypedFormControl(this.bidInterestRateTemp, [
          Validators.required,
          Validators.max(this.bidInterestRateTemp * 100),
          Validators.min(0),
        ]),
        transactionPassword: new UntypedFormControl(null),
        tos_1: new UntypedFormControl(false, Validators.pattern('true')),
        tos_2: new UntypedFormControl(false, Validators.pattern('true')),
        tos_3: new UntypedFormControl(false, Validators.pattern('true')),
        tos_4: new UntypedFormControl(false, Validators.pattern('true')),
      },
      {
        updateOn: 'change',
      },
    );

    if (this.auction.invoiceIasCompliant) {
      this.auctionBidFormGroup.get('bidInterestRate').disable();
    }
  }

  downloadInvoice(): void {
    this._buyerService.downloadInvoice(this.auction.id).subscribe(
      res => {
        this._httpService.openInBrowserFromAWSUrl(res);
      },
      err => {
        throw err;
      },
    );
  }

  downloadRating(userType: UserType): void {
    this._buyerService.downloadRating(this.auction.id, userType)
      .subscribe(
        res => {
          this._httpService.openInBrowserFromAWSUrl(res);
        },
        err => {
          throw err;
        },
      );
  }

  submitBid(): void {
    this.isSending = true;
    this.auctionBidFormGroup.disable();

    this._buyerService
      .sendBid(
        this.auction.id,
        this.bidInterestRateTemp,
        this.selectedFund ? this.selectedFund.id : 0,
        this.auctionBidFormGroup.get('transactionPassword').value,
      )
      .subscribe(
        _ => {
          this._dialogRef.close(true);
        },
        (err: Error) => {
          this.auctionBidFormGroup.enable();
          switch (err.message) {
            case 'WRONG_TRANSACTION_PASSWORD':
              this._messageService.showMessage(
                this._translateService.instant('ERROR.WRONG_TRANSACTION_PASSWORD'),
              );
              this.auctionBidFormGroup.get('transactionPassword').setErrors({ forbidden: true });
              break;
            default:
              this._messageService.showMessage(this._translateService.instant('ERROR.GENERAL'));
          }
        },
      );
  }

  downloadConditions(): void {
    this._buyerService
      .downloadAuctionConditions(this.auction.id, this.bidInterestRateTemp, this.selectedFund.id)
      .subscribe();
  }

  selectFund(fund: BuyerFundView): void {
    this.selectedFund = fund;
    this.updateWallet();
  }

  updateWallet(): void {
    if (!this.selectedFund) {
      return;
    }

    const fundId = this.selectedFund.id;
    const generation = ++this.walletRequestGeneration;

    this._buyerService.getFundWallet(fundId).subscribe(res => {
      if (generation !== this.walletRequestGeneration) {
        return;
      }
      this.wallet = res ?? undefined;
    });

    this._buyerService.getVirtualBalance(fundId).subscribe(res => {
      if (generation !== this.walletRequestGeneration) {
        return;
      }
      this.virtualBalance = res ?? undefined;
    });
  }

  get accontoLordo(): number {
    return this.auction?.nominalDownPaymentAmount ?? this.auction?.downPaymentAmount ?? 0;
  }

  get hasInsufficientLiquidity(): boolean {
    if (!this.virtualBalance) {
      return false;
    }
    return this.virtualBalance.virtualBalance < this.accontoLordo;
  }

  private hasInstallments(auction: AuctionView): boolean {
    const count = this.getInstallmentsCount(auction);
    if (count !== null) {
      return count > 1;
    }

    return this.readBoolean(auction, [
      'hasInstallments',
      'invoiceHasInstallments',
      'isInstallmentInvoice',
      'withInstallments',
    ]);
  }

  private getInstallmentsCount(source: unknown): number | null {
    if (!source || typeof source !== 'object') {
      return null;
    }

    const value = this.readValue(source as Record<string, unknown>, [
      'installmentsCount',
      'invoiceInstallmentsCount',
      'rateCount',
      'ratesCount',
      'numberOfInstallments',
      'installmentNumber',
    ]);

    if (Array.isArray(value)) {
      return value.length;
    }

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private readBoolean(source: unknown, keys: string[]): boolean {
    if (!source || typeof source !== 'object') {
      return false;
    }

    const value = this.readValue(source as Record<string, unknown>, keys);
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  private readValue(source: Record<string, unknown>, keys: string[]): unknown {
    for (const key of keys) {
      if (key in source) {
        return source[key];
      }
    }

    if ('invoice' in source && source.invoice && typeof source.invoice === 'object') {
      return this.readValue(source.invoice as Record<string, unknown>, keys);
    }

    return null;
  }
}

export interface AuctionBidDialogModel {
  auction: AuctionView;
}
